import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  ArrowLeftIcon,
  MapPinIcon,
  CalendarIcon,
  ClockIcon,
  UserGroupIcon,
  SparklesIcon,
  ShieldCheckIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const CreateDiningEvent = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingRestaurants, setLoadingRestaurants] = useState(true);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    restaurantId: '',
    eventDate: '',
    eventTime: '19:00',
    totalSeats: 4,
    requireFemale: false,
    genderBalance: 'preferred',
    description: ''
  });

  useEffect(() => {
    fetchRestaurants();
  }, []);

  // Pre-select restaurant from query parameter
  useEffect(() => {
    const restaurantId = searchParams.get('restaurantId');
    if (restaurantId && restaurants.length > 0) {
      setFormData(prev => ({
        ...prev,
        restaurantId: restaurantId
      }));
    }
  }, [searchParams, restaurants]);

  const fetchRestaurants = async () => {
    try {
      setLoadingRestaurants(true);
      const response = await axios.get(`${API_URL}/restaurants`);
      setRestaurants(response.data || []);
    } catch (error) {
      console.error('Error fetching restaurants:', error);
      setError('Failed to load restaurants. Please try again.');
    } finally {
      setLoadingRestaurants(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/dining-events/create`, formData);

      if (response.data.success) {
        navigate(`/dining-events/${response.data.event.id}`, {
          state: {
            message: 'Event created! Get ready to meet your neighbors!'
          }
        });
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create event. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const selectedRestaurant = restaurants.find(r => r._id === formData.restaurantId);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Header */}
      <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <button
            onClick={() => navigate('/dining-events')}
            className="flex items-center text-gray-400 hover:text-white mb-6 transition"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Events
          </button>

          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center">
              <SparklesIcon className="h-7 w-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold">
                Host a <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-rose-400">Connect</span> Event
              </h1>
              <p className="text-gray-400 mt-1">Bring neighbors together over great food</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6">
        {/* Preview Card */}
        {selectedRestaurant && (
          <div className="bg-gradient-to-r from-pink-500 to-rose-500 rounded-2xl p-6 mb-6 text-white shadow-xl">
            <p className="text-pink-100 text-sm mb-1">Your event at</p>
            <h3 className="text-2xl font-bold mb-2">{selectedRestaurant.name}</h3>
            <div className="flex items-center text-pink-100">
              <MapPinIcon className="h-4 w-4 mr-1" />
              <span className="text-sm">{selectedRestaurant.address || 'Address not available'}</span>
            </div>
            {formData.eventDate && (
              <div className="flex gap-4 mt-4 pt-4 border-t border-white/20">
                <div className="flex items-center gap-2">
                  <CalendarIcon className="h-5 w-5" />
                  <span>{new Date(formData.eventDate).toLocaleDateString('en-IN', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                </div>
                <div className="flex items-center gap-2">
                  <ClockIcon className="h-5 w-5" />
                  <span>{formData.eventTime}</span>
                </div>
                <div className="flex items-center gap-2">
                  <UserGroupIcon className="h-5 w-5" />
                  <span>{formData.totalSeats} seats</span>
                </div>
              </div>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6 pb-12">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl">
              {error}
            </div>
          )}

          {/* Restaurant Selection Card */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-pink-100 flex items-center justify-center">
                <MapPinIcon className="h-5 w-5 text-pink-600" />
              </div>
              <h2 className="text-lg font-bold text-gray-900">Choose Restaurant</h2>
            </div>

            {loadingRestaurants ? (
              <div className="w-full px-4 py-3 border border-gray-200 rounded-xl bg-gray-50 flex items-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-pink-600 mr-3"></div>
                <span className="text-gray-500">Loading restaurants...</span>
              </div>
            ) : restaurants.length === 0 ? (
              <div className="w-full px-4 py-3 border border-yellow-300 rounded-xl bg-yellow-50 text-yellow-800">
                No restaurants available. Please add restaurants first.
              </div>
            ) : (
              <select
                name="restaurantId"
                value={formData.restaurantId}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-pink-500 text-gray-900 bg-gray-50"
              >
                <option value="">Select a restaurant...</option>
                {restaurants.map(restaurant => (
                  <option key={restaurant._id} value={restaurant._id}>
                    {restaurant.name} {restaurant.cuisine ? `• ${restaurant.cuisine}` : ''}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Date & Time Card */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <CalendarIcon className="h-5 w-5 text-blue-600" />
              </div>
              <h2 className="text-lg font-bold text-gray-900">When</h2>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-2">Date</label>
                <input
                  type="date"
                  name="eventDate"
                  value={formData.eventDate}
                  onChange={handleChange}
                  min={new Date().toISOString().split('T')[0]}
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-pink-500 bg-gray-50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-2">Time</label>
                <input
                  type="time"
                  name="eventTime"
                  value={formData.eventTime}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-pink-500 bg-gray-50"
                />
              </div>
            </div>
          </div>

          {/* Group Size Card */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                <UserGroupIcon className="h-5 w-5 text-green-600" />
              </div>
              <h2 className="text-lg font-bold text-gray-900">Group Size</h2>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-600 mb-2">
                Total seats (including you)
              </label>
              <input
                type="number"
                name="totalSeats"
                value={formData.totalSeats}
                onChange={handleChange}
                min="2"
                max="20"
                required
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-pink-500 bg-gray-50"
              />
              <div className="flex gap-2 mt-3">
                {[2, 4, 6, 8].map(num => (
                  <button
                    key={num}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, totalSeats: num }))}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                      formData.totalSeats === num
                        ? 'bg-pink-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {num} people
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Safety Options Card */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                <ShieldCheckIcon className="h-5 w-5 text-purple-600" />
              </div>
              <h2 className="text-lg font-bold text-gray-900">Safety & Balance</h2>
            </div>

            <div className="space-y-4">
              <label className="flex items-start p-4 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition">
                <input
                  type="checkbox"
                  name="requireFemale"
                  checked={formData.requireFemale}
                  onChange={handleChange}
                  className="mt-0.5 rounded border-gray-300 text-pink-600 focus:ring-pink-500"
                />
                <div className="ml-3">
                  <span className="text-sm font-medium text-gray-900">
                    Require female participant
                  </span>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Event won't start until at least one female joins
                  </p>
                </div>
              </label>

              <div>
                <label className="block text-sm font-medium text-gray-600 mb-2">
                  Gender balance preference
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: 'none', label: 'No preference' },
                    { value: 'preferred', label: 'Preferred' },
                    { value: 'required', label: 'Required' }
                  ].map(option => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, genderBalance: option.value }))}
                      className={`px-4 py-2 rounded-xl text-sm font-medium transition ${
                        formData.genderBalance === option.value
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Description Card */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
                <ChatBubbleLeftRightIcon className="h-5 w-5 text-orange-600" />
              </div>
              <h2 className="text-lg font-bold text-gray-900">Description</h2>
              <span className="text-xs text-gray-400 ml-auto">Optional</span>
            </div>

            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="3"
              maxLength="500"
              placeholder="What should guests know? Cuisine preferences, dress code, conversation topics..."
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-pink-500 bg-gray-50 resize-none"
            />
            <p className="text-xs text-gray-400 mt-2 text-right">{formData.description.length}/500</p>
          </div>

          {/* Submit Section */}
          <div className="flex items-center gap-4 pt-4">
            <button
              type="button"
              onClick={() => navigate('/dining-events')}
              className="flex-1 px-6 py-4 border-2 border-gray-200 rounded-xl text-gray-700 font-semibold hover:bg-gray-50 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !formData.restaurantId || !formData.eventDate}
              className="flex-1 px-6 py-4 bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-xl font-semibold hover:from-pink-600 hover:to-rose-600 disabled:opacity-50 disabled:cursor-not-allowed transition shadow-lg shadow-pink-500/25"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Creating...
                </span>
              ) : (
                'Create Event'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateDiningEvent;
